import React from 'react';
import {
    Box,
    Typography,
    Container,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import {
    ContentDocument,
    ContentSection,
    ContentSubsection,
    ContentCategory
} from '../../types/contentTypes';

interface ContentRendererProps {
    data: ContentDocument;
    showContactInfo?: boolean;
    contactEmail?: string;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    headerAlignment?: 'left' | 'center' | 'right';
    logoSrc?: string;
    logoAlt?: string;
    showLogo?: boolean;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({
    data,
    showContactInfo = true,
    contactEmail = 'admin@thesampark.com',
    maxWidth = 'lg',
    headerAlignment = 'center',
    logoSrc = '/images/sampark-logo.png',
    logoAlt = 'Company Logo',
    showLogo = true
}) => {
    const { title, lastUpdated, sections } = data;

    const renderSubcategory = (subcategory: any, index: number) => (
        <Box key={index} sx={{ mb: 1, ml: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, textAlign: 'left' }}>
                {subcategory.name}
            </Typography>
            <List dense>
                {subcategory.items.map((item: string, subItemIndex: number) => (
                    <ListItem key={subItemIndex} sx={{ py: 0.25 }}>
                        <ListItemText
                            primary={`• ${item}`}
                            primaryTypographyProps={{ variant: 'body2' }}
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    const renderCategory = (category: ContentCategory, index: number) => (
        <Box key={index} sx={{ mb: 3, ml: { xs: 1, sm: 2 }, p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textAlign: 'left'
                }}
            >
                {category.name}
            </Typography>

            {category.description && (
                <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}>
                    {category.description}
                </Typography>
            )}

            {category.items && (
                <List dense>
                    {category.items.map((item: string, itemIndex: number) => (
                        <ListItem key={itemIndex} sx={{ py: 0.25 }}>
                            <ListItemText
                                primary={`• ${item}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            {category.subcategories && category.subcategories.map((subcategory, subIndex) =>
                renderSubcategory(subcategory, subIndex)
            )}
        </Box>
    );

    const renderSubsection = (subsection: ContentSubsection) => (
        <Box key={subsection.title} sx={{ mb: 4, ml: { xs: 1, sm: 2 }, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    color: 'primary.dark',
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    textAlign: 'left'
                }}
            >
                {subsection.title}
            </Typography>

            {subsection.description && (
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, color: 'text.primary', textAlign: 'left' }}>
                    {subsection.description}
                </Typography>
            )}

            {subsection.content && (
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, color: 'text.primary', textAlign: 'left' }}>
                    {subsection.content}
                </Typography>
            )}

            {subsection.items && (
                <List sx={{ pl: 2 }}>
                    {subsection.items.map((item: string, index: number) => (
                        <ListItem key={index} sx={{ py: 0.5, pl: 0 }}>
                            <ListItemText
                                primary={`• ${item}`}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    sx: { lineHeight: 1.6, color: 'text.primary' }
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            {subsection.categories && subsection.categories.map((category, index) =>
                renderCategory(category, index)
            )}

            {subsection.note && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary', textAlign: 'left' }}>
                    {subsection.note}
                </Typography>
            )}
        </Box>
    );

    const renderSection = (section: ContentSection, index: number) => (
        <Card
            key={index}
            sx={{
                mb: 4,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'box-shadow 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
                }
            }}
        >
            <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 'bold',
                        mb: 3,
                        color: 'primary.main',
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        lineHeight: 1.3,
                        textAlign: 'left'
                    }}
                >
                    {section.heading}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        mb: 4,
                        lineHeight: 1.7,
                        whiteSpace: 'pre-line',
                        fontSize: '1.1rem',
                        color: 'text.primary',
                        textAlign: 'left'
                    }}
                >
                    {section.description}
                </Typography>

                {section.subsections && section.subsections.map((subsection) => renderSubsection(subsection))}

                {section.items && (
                    <List dense>
                        {section.items.map((item: string, itemIndex: number) => (
                            <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                                <ListItemText
                                    primary={`• ${item}`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

                {section.rights && (
                    <List dense>
                        {section.rights.map((right: string, rightIndex: number) => (
                            <ListItem key={rightIndex} sx={{ py: 0.5 }}>
                                <ListItemText
                                    primary={`• ${right}`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

                {section.contact && (
                    <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold', textAlign: 'left' }}>
                        {section.contact}
                    </Typography>
                )}

                {section.note && (
                    <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary', textAlign: 'left' }}>
                        {section.note}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );

    return (
        <>
            {showLogo && (
                <Box
                    sx={{
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        py: 2
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', paddingLeft: 3, alignItems: 'center', ml: -1 }}>
                        <img
                            src={logoSrc}
                            alt={logoAlt}
                            style={{
                                height: '60px',
                                width: 'auto'
                            }}
                            onError={(e) => {
                                const target = e.currentTarget;
                                const nextSibling = target.nextElementSibling as HTMLElement | null;
                                if (target && nextSibling) {
                                    target.style.display = 'none';
                                    nextSibling.style.display = 'block';
                                }
                            }}
                        />
                    </Box>
                </Box>
            )}

            {/* Scrollable Content */}
            <Box sx={{ height: 'calc(100vh - 95px)', overflow: 'auto', bgcolor: 'background.default', pt: showLogo ? '20px' : 0 }}>
                <Container maxWidth={maxWidth} sx={{ py: 2, px: { xs: 2, sm: 1, md: 2 } }}>
                    <Box sx={{ mb: 2, textAlign: headerAlignment }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 'bold',
                                mb: 3,
                                color: 'primary.main',
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                lineHeight: 1.2
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 6, borderColor: 'divider' }} />

                    <Box>
                        {sections.map((section, index) => renderSection(section, index))}
                    </Box>

                    {showContactInfo && (
                        <Box
                            sx={{
                                mt: 6,
                                p: { xs: 3, sm: 4 },
                                bgcolor: 'primary.light',
                                borderRadius: 3,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                border: '1px solid',
                                borderColor: 'primary.main'
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 'bold',
                                    mb: 2,
                                    color: 'primary.dark',
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}
                            >
                                Contact Information
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    lineHeight: 1.6,
                                    color: 'primary.dark'
                                }}
                            >
                                If you have any questions about this content, please contact us at:{' '}
                                <a
                                    href={`mailto:${contactEmail}`}
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'underline',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {contactEmail}
                                </a>
                            </Typography>
                        </Box>
                    )}
                </Container>
            </Box>
        </>
    );
};

export default ContentRenderer;